<script setup lang="ts">
import { ref, watch } from 'vue';

import type { MapTypeId } from '@libs/maps';
import {
  AppAlternativeFilterButton,
  AppFlicker,
  AppIconButton,
  AppLoadingDots,
} from '@libs/ui';

import SearchByMovingMapToggle from '../searchByMovingMapToggle/SearchByMovingMapToggle.vue';

import MapSettingsDrawer from './MapSettingsDrawer.vue';

interface ProductSelectionMapLayoutProps {
  shouldDisplayLoader: boolean;
  mapLayout: 'mobile' | 'desktop';
  mapType: MapTypeId;
  isDesktopMapFullScreen: boolean;
  shouldSearchByMapMovement: boolean;
  disableShouldSearchByMapMovement: boolean;
  activeFiltersCount: number;
}

const props = defineProps<ProductSelectionMapLayoutProps>();

interface ProductSelectionMapLayoutEmits {
  (eventName: 'toggle-search-by-moving-map'): void;
  (eventName: 'toggle-full-screen'): void;
  (eventName: 'close-map'): void;
  (eventName: 'open-filters'): void;
  (eventName: 'set-map-type', mapLayoutId: MapTypeId): void;
}

const emit = defineEmits<ProductSelectionMapLayoutEmits>();

const isFirstLoading = ref<boolean>(true);
const isDrawerShown = ref<boolean>(false);

watch(
  () => props.shouldDisplayLoader,
  (isLoading) => {
    if (!isLoading && isFirstLoading.value) {
      isFirstLoading.value = false;
    }
  },
  // TODO:
  // Removing { immediate: true } fixes missing initial AppFlicker on desktop, but breaks mobile map.
  // This requires further investigation and fixing.
  { immediate: true }
);

function toggleDrawer(): void {
  isDrawerShown.value = !isDrawerShown.value;
}
</script>

<template>
  <div class="product-selection-map-layout">
    <!-- DESKTOP LAYOUT -->
    <SearchByMovingMapToggle
      v-if="props.mapLayout === 'desktop'"
      :is-loading="props.shouldDisplayLoader"
      :search-by-moving-map="props.shouldSearchByMapMovement"
      :is-disabled="props.disableShouldSearchByMapMovement"
      class="search-by-moving-map-desktop"
      @toggle-search-by-moving-map="emit('toggle-search-by-moving-map')"
    />

    <AppIconButton
      v-if="props.mapLayout === 'desktop'"
      class="action-button-desktop"
      type="secondary"
      state="enabled"
      size="medium"
      :ariaLabel="$t('a11y.open_image_gallery')"
      :icon="{
        name: props.isDesktopMapFullScreen ? 'close' : 'zoom',
      }"
      @click="emit('toggle-full-screen')"
    />
    <!-- END OF DESKTOP LAYOUT -->

    <!-- MOBILE LAYOUT -->
    <div
      v-if="props.shouldDisplayLoader && props.mapLayout === 'mobile'"
      class="loading-dots-wrapper-mobile"
    >
      <AppLoadingDots type="black" />
    </div>

    <div v-if="props.mapLayout === 'mobile'" class="map-header-mobile">
      <div class="actions">
        <AppIconButton
          type="modern"
          state="enabled"
          :ariaLabel="$t('a11y.show_map_settings')"
          size="small"
          :icon="{
            name: 'layersOutlined',
          }"
          @click="isDrawerShown = true"
        />

        <AppAlternativeFilterButton
          :text="$t('filter.header')"
          class="filters-button"
          type="modern"
          :compact="false"
          :active-filters-amount="props.activeFiltersCount"
          :show-filters-aria-label="$t('a11y.show_filters')"
          @click="emit('open-filters')"
        />
      </div>

      <AppIconButton
        type="text"
        state="enabled"
        size="medium"
        :icon="{ name: 'close' }"
        :ariaLabel="$t('a11y.close_panel')"
        @click="emit('close-map')"
      />
    </div>

    <MapSettingsDrawer
      v-if="isDrawerShown"
      :search-by-moving-map="props.shouldSearchByMapMovement"
      :map-type="props.mapType"
      @toggle-search-by-moving-map="emit('toggle-search-by-moving-map')"
      @close="toggleDrawer()"
      @change-map-type="emit('set-map-type', $event)"
    />
    <!-- END OF MOBILE LAYOUT -->

    <AppFlicker
      v-if="props.shouldDisplayLoader && isFirstLoading"
      width="100%"
      height="100%"
      border-radius="0"
    />
    <template v-else>
      <slot name="map" />
    </template>

    <div class="product-card">
      <slot name="product-card" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.product-selection-map-layout {
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;

  .search-by-moving-map-desktop {
    position: absolute;
    top: var(--spacing-12);
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;
  }

  .action-button-desktop {
    position: absolute;
    top: var(--spacing-12);
    right: var(--spacing-12);
    z-index: 2;
  }

  .loading-dots-wrapper-mobile {
    border-radius: var(--border-radius-8);
    background-color: var(--color-white);
    height: 36px;
    box-shadow: var(--shadow-elevation-20);

    position: absolute;
    top: var(--spacing-80);
    left: 50%;
    transform: translateX(-50%) scale(1.2);
    z-index: 2;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  .map-header-mobile {
    position: absolute;
    z-index: 2;
    left: 0;
    top: 0;
    height: 60px;
    width: 100%;
    background-color: var(--color-white);

    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 var(--spacing-12);
    border-bottom: 1px solid var(--color-neutral-30);
    box-shadow: var(--shadow-elevation-20);

    .actions {
      display: flex;
      align-items: center;
      gap: var(--spacing-12);
    }
  }

  .product-card {
    position: absolute;
    bottom: var(--spacing-16);
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;
  }
}
</style>
